import { AdminLayout } from "components/admin/admin-layout";
import { OrderDetails } from "components/admin/orders/manual/order-details";
import { Button } from "components/buttons/button";
import { CountrySelector } from "components/forms/country-select";
import { StandardInput } from "components/forms/input";
import { ShippingSelectorValue } from "components/forms/shipping-selector";
import {
  createEmptyState,
  saveDraftContent,
} from "components/text-editor/editor-utils";
import { TextEditor } from "components/text-editor/text-editor";
import currency from "currency.js";
import { EditorState } from "draft-js";
import {
  InsertManualOrderMutationVariables,
  Order_Status_Enum,
} from "lib/graphql-generated";
import { useInput } from "lib/hooks/use-input";
import { emailRegex, getToken } from "lib/utils";
import { useRouter } from "next/router";
import { useReducer } from "react";

export const itemTypes = [
  "product",
  "book",
  "custom",
  "voucher",
  "pif",
] as const;
type LineItemFields = "type" | "data" | "price" | "quantity" | "discount";
export class LineItem {
  type: "product" | "book" | "custom" | "voucher" | "pif";
  data: {
    id: string;
    name: string;
    price: number;
  };
  price: number;
  quantity: number;
  discount: number;

  constructor() {
    this.type = null;
    this.data = {
      id: "",
      name: "",
      price: 0,
    };
    this.price = 0;
    this.quantity = this.type === "custom" ? 1 : 0;
    this.discount = 0;
  }

  set(field: LineItemFields, value) {
    this[field as any] = value;
  }

  totalPrice(asInt = false) {
    if (asInt) {
      return (
        (this.data.price - currency(this.discount).intValue) * this.quantity
      );
    } else {
      return currency(
        (this.data.price - currency(this.discount).intValue) * this.quantity,
        {
          fromCents: true,
        }
      ).value;
    }
  }
}

export type Action =
  | { type: "removeItem"; index: number }
  | { type: "addItem" }
  | {
      type: "modifyItem";
      index: number;
      field: "type" | "data" | "price" | "quantity" | "discount";
      value: string | number | { id: string; name: string; price: number };
    };

function lineItemReducer(state: LineItem[], action: Action): LineItem[] {
  switch (action.type) {
    case "addItem":
      return [...state, new LineItem()];
    case "removeItem":
      return state.filter((_, index) => index !== action.index);
    case "modifyItem":
      return state.map((item, index) => {
        if (index === action.index) {
          item.set(action.field, action.value);
        }
        return item;
      });
    default:
      throw new Error("No such action type");
  }
}

export const ManualOrder = () => {
  const router = useRouter();
  const fulfillment = useInput<ShippingSelectorValue>({
    id: "",
    name: "",
    price: 0,
  });
  const name = useInput<string>("");
  const email = useInput<string>("");
  const phone = useInput<string>("");
  const line1 = useInput<string>("");
  const line2 = useInput<string>("");
  const line3 = useInput<string>("");
  const region = useInput<string>("");
  const city = useInput<string>("");
  const code = useInput<string>("");
  const country = useInput<string>("");
  const [lineItems, dispatch] = useReducer(lineItemReducer, []);
  const note = useInput<EditorState>(createEmptyState());

  const canAddItem =
    lineItems.length === 0
      ? true
      : !lineItems.some((item) => {
          switch (item.type) {
            case "book":
              return !(item.quantity > 0 && item.data.id !== "");
            case "product":
              return !(item.quantity > 0 && item.data.id !== "");
            case "voucher":
              return !(item.data.price > 0 && emailRegex.test(item.data.name));
            case "pif":
              return !(item.data.price !== 0);
            case "custom":
              return !(item.data.name !== "" && item.data.price > 0);
            default:
              return true;
          }
        });

  const canSave =
    canAddItem &&
    name.isModified &&
    email.isModified &&
    phone.isModified &&
    emailRegex.test(email.value) &&
    country.isModified &&
    lineItems.length > 0 &&
    fulfillment.isModified;

  const handleSave = (sendInvoice: boolean) => {
    const data: InsertManualOrderMutationVariables["data"] = {
      status: sendInvoice ? Order_Status_Enum.Draft : Order_Status_Enum.New,
      notes: saveDraftContent(note.value),
      address: {
        data: {
          name: name.value,
          phone_number: phone.value,
          line_1: line1.value,
          line_2: line2.value,
          line_3: line3.value,
          region: region.value,
          city: city.value,
          code: code.value,
          country_code: country.value,
          contact_email: email.value,
        },
      },
      shipping_option_id: fulfillment.value.id,
      order_books: {
        data: lineItems
          .filter((i) => i.type === "book")
          .map((b) => ({
            book_isbn: b.data.id,
            quantity: b.quantity,
            price: b.data.price,
            discount: currency(b.discount).intValue,
          })),
      },
      order_products: {
        data: lineItems
          .filter((i) => i.type === "product")
          .map((p) => ({
            product_id: p.data.id,
            price: p.data.price,
            quantity: p.quantity,
            discount: currency(p.discount).intValue,
          })),
      },
      custom_line_price: lineItems.filter((i) => i.type === "custom")[0]?.data
        .price,
      custom_line_title: lineItems.filter((i) => i.type === "custom")[0]?.data
        .name,
      custom_shipping_price:
        fulfillment.value.id === "custom" ? fulfillment.value.price : null,
      pif_donations: {
        data: lineItems.some((item) => item.type === "pif")
          ? [
              {
                amount: lineItems.find((item) => item.type === "pif").data
                  .price,
              },
            ]
          : [],
      },
      vouchers: {
        data: lineItems
          .filter((item) => item.type === "voucher")
          .map((voucher) => ({
            recipient: voucher.data.name,
            initial_amount: voucher.data.price,
          })),
      },
    };

    const formData = new FormData();
    formData.append("data", JSON.stringify(data));
    fetch("/api/stripe/manual-order", {
      method: "PUT",
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((data) => {
        router.push(`/admin/orders/${data.orderId}`);
      });
  };

  return (
    <AdminLayout page='Manual Order'>
      <article className='space-y-4'>
        <header className='flex flex-row justify-between'>
          <h1 className='header text-4xl'>Creating Manual Order</h1>
          <section className='space-x-4'>
            <button
              className='underline text-sm hover:text-green-default disabled:text-gray-400 disabled:cursor-not-allowed'
              onClick={() => handleSave(false)}
              disabled={!canSave}>
              Save - no invoice / confirmation
            </button>
            <Button
              buttonType='primary'
              buttonSize='md'
              disabled={!canSave}
              onClick={(e) => handleSave(true)}>
              Save & Send Invoice
            </Button>
            <Button buttonType='ghost' buttonSize='md' href='/admin/orders'>
              Cancel
            </Button>
          </section>
        </header>
        <form className='flex flex-col  w-full space-y-4 lg:space-y-8'>
          <fieldset className='flex flex-row flex-wrap w-full space-y-4  lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-4 border-t border-gray-500'>
            <legend className='pr-4 text-gray-500 header text-xl'>
              Recipient
            </legend>
            <StandardInput
              label='Name'
              id='name'
              type='text'
              bind={{ ...name.bind }}
              className=''
            />
            <StandardInput
              label='Contact Email'
              id='email'
              name='email'
              type='email'
              bind={{ ...email.bind }}
            />
            <StandardInput
              label='Contact Number'
              id='phone'
              name='phone'
              type='phone'
              bind={{ ...phone.bind }}
            />
            <StandardInput
              label='Line 1'
              id='line1'
              type='text'
              bind={{ ...line1.bind }}
            />
            <StandardInput
              label='Line 2'
              id='line2'
              type='text'
              bind={{ ...line2.bind }}
            />
            <StandardInput
              label='Line 3'
              id='line3'
              type='text'
              bind={{ ...line3.bind }}
            />
            <StandardInput
              label='Region'
              id='region'
              type='text'
              bind={{ ...region.bind }}
            />
            <StandardInput
              label='City'
              id='city'
              type='text'
              bind={{ ...city.bind }}
            />
            <StandardInput
              label='Post Code'
              id='code'
              type='text'
              bind={{ ...code.bind }}
            />
            <CountrySelector
              label='Country'
              value={country.value}
              setValue={country.setValue}
              className=''
            />
          </fieldset>
          <fieldset className='col-span-full border-t flex flex-col space-y-8 border-gray-500'>
            <legend className='pr-4 text-gray-500 header text-xl'>Notes</legend>

            <TextEditor
              state={note.value}
              setState={note.setValue}
              disableToolbar
            />
          </fieldset>
          <OrderDetails
            canAddItem={canAddItem}
            country={country}
            dispatch={dispatch}
            fulfillment={fulfillment}
            lineItems={lineItems}
          />
        </form>
      </article>
    </AdminLayout>
  );
};

export default ManualOrder;
